import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { Search, Loader2, ArrowRight, UserPlus } from 'lucide-react';
import CenteredStatusPopup from '@/components/merchant/CenteredStatusPopup';
import {
  apiMerchantDashboard,
  apiMerchantCustomerActivity,
  apiMerchantAddCustomer,
} from '@/services/api';

const statusMeta = {
  vip_regular: { label: 'VIP Regular', className: 'bg-emerald-100 text-emerald-700' },
  steady_diner: { label: 'Steady Diner', className: 'bg-emerald-100 text-emerald-700' },
  cooling_down: { label: 'Cooling Down', className: 'bg-amber-100 text-amber-700' },
  missing_you: { label: 'Missing You', className: 'bg-rose-100 text-rose-700' },
  dormant: { label: 'Dormant', className: 'bg-stone-200 text-stone-700' },
  first_taste: { label: 'First Taste', className: 'bg-cyan-100 text-cyan-700' },
};

const HEALTH_COLORS = ['#22c55e', '#818cf8', '#eab308', '#ef4444'];

function HealthTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-lg border border-stone-200 bg-white px-3 py-2 shadow-sm">
      <p className="text-xs font-semibold text-stone-700">{row.key}</p>
      <p className="text-xs text-stone-500 mt-0.5">{row.value} customers ({row.percent}%)</p>
    </div>
  );
}

function downloadCsv(filename, rows) {
  const body = rows.map((r) => r.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function CustomersOldPage() {
  const [dashboard, setDashboard] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedSideCustomerId, setSelectedSideCustomerId] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [sideDetails, setSideDetails] = useState(null);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [toast, setToast] = useState('');
  const [busyAction, setBusyAction] = useState('');

  const customers = useMemo(() => dashboard?.customers || [], [dashboard?.customers]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(q));
  }, [customers, search]);

  const selectedSideCustomer = useMemo(
    () => customers.find((c) => c.id === selectedSideCustomerId) || null,
    [customers, selectedSideCustomerId]
  );

  const setToastMessage = (message) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3800);
  };

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const d = await apiMerchantDashboard();
      setDashboard(d);
      if (!selectedSideCustomerId && d?.customers?.length) setSelectedSideCustomerId(d.customers[0].id);
    } catch (e) {}
    if (!silent) setLoading(false);
  }, [selectedSideCustomerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => fetchData(true), 4000);
    return () => clearInterval(timer);
  }, [fetchData]);

  const fetchCustomerDetails = useCallback(async (customerId, forModal = false) => {
    if (!customerId) return;
    if (forModal) setDetailsLoading(true);
    try {
      const activity = await apiMerchantCustomerActivity(customerId);
      if (forModal) setDetails(activity);
      setSideDetails(activity);
    } catch {
      if (forModal) setDetails(null);
      setSideDetails(null);
    }
    if (forModal) setDetailsLoading(false);
  }, []);

  useEffect(() => {
    if (!selectedSideCustomerId) return;
    fetchCustomerDetails(selectedSideCustomerId, false);
  }, [fetchCustomerDetails, selectedSideCustomerId]);

  const openDetails = async (customerId) => {
    setSelectedSideCustomerId(customerId);
    setDetailsOpen(true);
    await fetchCustomerDetails(customerId, true);
  };

  const runAddCustomer = async () => {
    if (!newPhone.trim()) {
      setToastMessage('Phone number is required.');
      return;
    }
    setBusyAction('add-customer');
    try {
      const res = await apiMerchantAddCustomer(newName.trim(), newPhone.trim());
      setNewName('');
      setNewPhone('');
      await fetchData(true);
      if (res?.already_exists) setToastMessage('Customer already exists for this restaurant.');
      else setToastMessage('Customer added successfully.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToastMessage(typeof detail === 'string' ? detail : 'Unable to add customer.');
    }
    setBusyAction('');
  };

  const healthCounts = useMemo(() => {
    const result = { loyal: 0, new: 0, risk: 0, inactive: 0 };
    customers.forEach((c) => {
      if (c.status === 'vip_regular' || c.status === 'steady_diner') result.loyal += 1;
      else if (c.status === 'first_taste') result.new += 1;
      else if (c.status === 'cooling_down') result.risk += 1;
      else result.inactive += 1;
    });
    return result;
  }, [customers]);

  const healthData = useMemo(
    () => [
      { key: 'Loyal & Active', value: healthCounts.loyal, percent: customers.length ? Math.round((healthCounts.loyal / customers.length) * 100) : 0, className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
      { key: 'New (Last 30d)', value: healthCounts.new, percent: customers.length ? Math.round((healthCounts.new / customers.length) * 100) : 0, className: 'border-violet-200 bg-violet-50 text-violet-700' },
      { key: 'At Risk', value: healthCounts.risk, percent: customers.length ? Math.round((healthCounts.risk / customers.length) * 100) : 0, className: 'border-amber-200 bg-amber-50 text-amber-700' },
      { key: 'Inactive', value: healthCounts.inactive, percent: customers.length ? Math.round((healthCounts.inactive / customers.length) * 100) : 0, className: 'border-rose-200 bg-rose-50 text-rose-700' },
    ],
    [customers.length, healthCounts.inactive, healthCounts.loyal, healthCounts.new, healthCounts.risk]
  );

  const exportCustomersCsv = () => {
    const rows = [
      ['Customer', 'Phone', 'Active Points', 'Lifetime Earned Points', 'Last Visit', 'Status'],
      ...filtered.map((c) => [
        c.name || '',
        c.phone || '',
        c.points || 0,
        c.total_earned_points || 0,
        c.last_visit ? new Date(c.last_visit).toISOString() : '',
        statusMeta[c.status]?.label || 'First Taste',
      ]),
    ];
    downloadCsv('flyback-customers.csv', rows);
  };

  const exportCustomerTransactionsCsv = () => {
    if (!details?.customer) return;
    const rows = [
      ['Customer', details.customer.name || ''],
      ['Phone', details.customer.phone || ''],
      [],
      ['Date', 'Type', 'Title', 'Detail', 'Points', 'Amount', 'Order ID'],
      ...(details.transactions || []).map((tx) => [
        tx.created_at ? new Date(tx.created_at).toISOString() : '',
        tx.type || '',
        tx.title || '',
        tx.detail || '',
        tx.points || 0,
        tx.amount || '',
        tx.order_id || '',
      ]),
    ];
    downloadCsv(`${String(details.customer.name || 'customer').replace(/\s+/g, '-').toLowerCase()}-transactions.csv`, rows);
  };

  const activePoints = selectedSideCustomer?.points || 0;
  const lifetimePoints = selectedSideCustomer?.total_earned_points || 0;
  const recentActivities = sideDetails?.transactions?.slice(0, 6) || [];

  return (
    <div className="space-y-5" data-testid="customers-page">
      <CenteredStatusPopup message={toast} />

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            Customers
          </h1>
          <p className="text-sm text-stone-500">
            View and manage your loyalty program members.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCustomersCsv} className="h-10 px-4 rounded-xl border border-stone-200 bg-white text-sm font-semibold">
            Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr,320px] gap-4">
        <div className="space-y-4">
          <div className="rounded-2xl border border-stone-200 bg-white p-4">
            <p className="text-xl font-semibold">Customer Health Overview</p>
            <p className="text-xs text-stone-500">Real-time segmentation of your database</p>
            <div className="grid grid-cols-1 lg:grid-cols-[340px,1fr] gap-4 mt-4">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={healthData} dataKey="value" nameKey="key" innerRadius={62} outerRadius={95} strokeWidth={0}>
                      {healthData.map((entry, idx) => (
                        <Cell key={entry.key} fill={HEALTH_COLORS[idx % HEALTH_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<HealthTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="-mt-36 text-center pointer-events-none">
                  <p className="text-xs text-stone-500">Total Customers</p>
                  <p className="text-4xl font-bold text-[#0f172a]">{customers.length.toLocaleString()}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {healthData.map((item, idx) => (
                  <div key={item.key} className={`rounded-xl border px-3 py-2 ${item.className}`}>
                    <div className="flex items-center justify-between text-xs font-semibold">
                      <span>{item.key}</span>
                      <span>{item.percent}%</span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-3xl font-bold">{item.value}</p>
                      <button className="text-xs font-semibold flex items-center gap-1">{item.cta} </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-stone-200 bg-white p-4 space-y-2">
            <p className="font-semibold text-sm">Add Customer</p>
            <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,160px] gap-2">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Customer name" className="h-9 rounded-lg text-xs" />
              <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="Phone number" className="h-9 rounded-lg text-xs" />
              <button onClick={runAddCustomer} disabled={busyAction === 'add-customer'} className="h-9 rounded-lg gradient-brand-btn text-xs font-semibold flex items-center justify-center gap-1">
                {busyAction === 'add-customer' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-3.5 h-3.5" /> Create</>}
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
            <div className="p-4 border-b border-stone-100 flex items-center justify-between gap-3">
              <div className="relative w-full max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, or email..." className="h-10 rounded-xl pl-10" data-testid="customer-search" />
              </div>
              <p className="text-xs text-stone-500">{filtered.length} customers</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-stone-100">
                    {['Customer', 'Points', 'Lifetime Earned Points', 'Last Visit', 'Status'].map((h) => (
                      <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-stone-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i}><td colSpan={5} className="px-4 py-4"><div className="h-4 rounded bg-stone-100 animate-pulse" /></td></tr>
                  )) : filtered.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-stone-500">No customers found.</td></tr>
                  ) : filtered.map((c) => (
                    <tr
                      key={c.id}
                      className={`border-b border-stone-100 hover:bg-stone-50/60 cursor-pointer ${selectedSideCustomerId === c.id ? 'bg-brand-soft/40' : ''}`}
                      onClick={() => openDetails(c.id)}
                    >
                      <td className="px-4 py-3">
                        <p className="font-semibold">{c.name}</p>
                        <p className="text-xs text-stone-500">{String(c.phone || '')}</p>
                      </td>
                      <td className="px-4 py-3 font-semibold">{(c.points || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 font-semibold">{(c.total_earned_points || 0).toLocaleString()} pts</td>
                      <td className="px-4 py-3 text-stone-500">{c.last_visit ? new Date(c.last_visit).toLocaleDateString() : 'N/A'}</td>
                      <td className="px-4 py-3">
                        <Badge className={`rounded-full text-xs border-0 ${statusMeta[c.status]?.className || statusMeta.first_taste.className}`}>
                          {statusMeta[c.status]?.label || 'First Taste'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white p-4 h-fit">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-stone-100 flex items-center justify-center text-sm font-bold text-stone-600">
              {(selectedSideCustomer?.name || 'C').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-bold text-lg truncate">{selectedSideCustomer?.name || 'Select Customer'}</p>
              <p className="text-xs text-stone-500">{String(selectedSideCustomer?.phone || '')}</p>
            </div>
            {selectedSideCustomer && (
              <Badge className={`rounded-full text-xs border-0 ${statusMeta[selectedSideCustomer.status]?.className || statusMeta.first_taste.className}`}>
                {statusMeta[selectedSideCustomer.status]?.label || 'First Taste'}
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="rounded-xl border border-stone-200 p-3">
              <p className="text-[11px] text-stone-500 uppercase tracking-wider font-semibold">Points Balance</p>
              <p className="text-2xl font-bold mt-1">{activePoints.toLocaleString()}</p>
            </div>
            <div className="rounded-xl border border-stone-200 p-3">
              <p className="text-[11px] text-stone-500 uppercase tracking-wider font-semibold">Lifetime Earned</p>
              <p className="text-2xl font-bold mt-1">{lifetimePoints.toLocaleString()}</p>
            </div>
          </div>

          <div className="rounded-xl border border-brand/20 bg-brand-soft p-3 mt-4">
            <p className="text-xs font-semibold text-brand">AI Insight</p>
            <p className="text-xs text-stone-600 mt-1">
              {selectedSideCustomer
                ? `${selectedSideCustomer.name} typically visits during peak hours. A targeted offer can improve weekly spend.`
                : 'Select a customer to see personalized insight.'}
            </p>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">Recent Activity</p>
            </div>
            <div className="mt-2 space-y-2">
              {recentActivities.length === 0 && <p className="text-xs text-stone-500">No recent activity for selected customer.</p>}
              {recentActivities.map((tx) => (
                <div key={tx.id} className="rounded-lg border border-stone-200 p-2">
                  <p className="text-xs font-semibold">{tx.title}</p>
                  <p className="text-[11px] text-stone-500 mt-0.5">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''}</p>
                  <p className={`text-xs font-semibold mt-1 ${tx.points >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {tx.points >= 0 ? '+' : ''}{tx.points} pts
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="w-[96vw] max-w-4xl max-h-[85vh] overflow-y-auto rounded-2xl" data-testid="customer-details-modal">
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-lg font-bold">Customer Activity</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">Detailed points, transactions, and visit history.</DialogDescription>
            </div>
            <button
              type="button"
              onClick={exportCustomerTransactionsCsv}
              disabled={!details?.transactions?.length}
              className="h-9 px-3 rounded-lg border border-stone-200 bg-white text-xs font-semibold disabled:opacity-50"
            >
              Export Transactions CSV
            </button>
          </div>
          {detailsLoading && <div className="h-52 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}
          {!detailsLoading && !details && <p className="text-sm text-muted-foreground py-6">Unable to load customer details.</p>}
          {!detailsLoading && details && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="rounded-xl border border-border p-3"><p className="text-[11px] text-muted-foreground">Name</p><p className="text-sm font-semibold mt-1">{details.customer.name}</p></div>
                <div className="rounded-xl border border-border p-3"><p className="text-[11px] text-muted-foreground">Balance</p><p className="text-sm font-semibold mt-1">{details.summary.points_balance.toLocaleString()} pts</p></div>
                <div className="rounded-xl border border-border p-3"><p className="text-[11px] text-muted-foreground">Points Earned</p><p className="text-sm font-semibold mt-1 text-emerald-600">{details.summary.points_earned.toLocaleString()}</p></div>
                <div className="rounded-xl border border-border p-3"><p className="text-[11px] text-muted-foreground">Visits</p><p className="text-sm font-semibold mt-1">{details.summary.visits}</p></div>
              </div>
              <div className="rounded-xl border border-border p-3">
                <p className="text-sm font-semibold mb-2">Visit History</p>
                <div className="max-h-32 overflow-auto space-y-1">
                  {(details.visit_history || []).slice(0, 15).map((v, idx) => (
                    <div key={`${v.date}-${idx}`} className="text-xs text-muted-foreground flex justify-between">
                      <span>{v.date ? new Date(v.date).toLocaleString() : 'N/A'}</span>
                      <span>{v.order_id ? `Order #${v.order_id}` : 'Visit'} • +{v.points} pts</span>
                    </div>
                  ))}
                  {(details.visit_history || []).length === 0 && <p className="text-xs text-muted-foreground">No visits yet.</p>}
                </div>
              </div>
              <div className="rounded-xl border border-border p-3">
                <p className="text-sm font-semibold mb-2">Recent Transactions</p>
                <div className="max-h-60 overflow-auto divide-y divide-border">
                  {(details.transactions || []).slice(0, 20).map((t) => (
                    <div key={t.id} className="py-2.5 text-xs flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm">{t.title}</p>
                        {t.detail && <p className="text-muted-foreground mt-0.5 text-xs whitespace-pre-wrap break-words">{t.detail}</p>}
                      </div>
                      <div className="shrink-0 text-right">
                        <p className={`font-semibold ${t.points >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{t.points >= 0 ? '+' : ''}{t.points} pts</p>
                        <p className="text-muted-foreground">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
